// ----------------------------------------------------------------------

export function remToPx(value) {
  return Math.round(parseFloat(value) * 16);
}

export function pxToRem(value) {
  return `${value / 16}rem`;
}

export function responsiveFontSizes({ sm, md, lg }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

// ----------------------------------------------------------------------

const FONT_PRIMARY = 'Public Sans, sans-serif'; // Google Font
// const FONT_SECONDARY = 'CircularStd, sans-serif'; // Local Font

const getTypographyConfig = (customTypography = {}) => {
  // Extract custom values with defaults
  const headings = customTypography.headings || { fontSize: '1.5', fontWeight: 'bold' };
  const columnHeadings = customTypography.columnHeadings || { fontSize: '0.875', fontWeight: 'bold' };
  const subheadings = customTypography.subheadings || { fontSize: '1.125', fontWeight: 'normal' };
  const labels = customTypography.labels || { fontSize: '0.875', fontWeight: 'normal' };
  const toastText = customTypography.toastText || { fontSize: '0.875', fontWeight: 'normal' };
  const body = customTypography.body || customTypography.bodyText || { fontSize: '0.875', fontWeight: 'normal' };
  // Note: field, cell, and captions are defined in TYPOGRAPHY_INVENTORY for future customization
  // but currently default to body and labels for backward compatibility
  // const field = customTypography.field || customTypography.bodyText || { fontSize: '0.875', fontWeight: 'normal' };
  // const cell = customTypography.cell || customTypography.bodyText || { fontSize: '0.875', fontWeight: 'normal' };
  // const captions = customTypography.captions || customTypography.labels || { fontSize: '0.875', fontWeight: 'normal' };

  // Convert rem string to px for responsive calculations with validation
  const parseRemToPx = (remValue, fallback = 16) => {
    const parsed = parseFloat(remValue);
    return !isNaN(parsed) && parsed > 0 ? parsed * 16 : fallback;
  };

  // Safe parseFloat with fallback for rem values
  const safeParseRem = (remValue, fallback) => {
    const parsed = parseFloat(remValue);
    return !isNaN(parsed) && parsed > 0 ? parsed : parseFloat(fallback);
  };

  const headingFontSizeNum = safeParseRem(headings.fontSize, '1.5');
  const subheadingFontSizeNum = safeParseRem(subheadings.fontSize, '1.125');
  const headingBasePx = parseRemToPx(headings.fontSize, 24);
  const headingH2Multiplier = 0.85;
  const headingH3Multiplier = 0.7;
  const headingH4Multiplier = 0.6;
  const headingH5Multiplier = 0.55;
  const headingH6Multiplier = 0.5;
  const subheadingSubtitle2Multiplier = 0.875;

  // Map font weight strings to numeric values
  const getFontWeight = (weight) => {
    if (weight === 'bold') return 700;
    return 400;
  };

  return {
    fontFamily: FONT_PRIMARY,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontWeight: getFontWeight(headings.fontWeight),
      lineHeight: 80 / 64,
      fontSize: `${headings.fontSize}rem`,
      ...responsiveFontSizes({ 
        sm: headingBasePx * 1.3, 
        md: headingBasePx * 1.45, 
        lg: headingBasePx * 1.6 
      }),
    },
    h2: {
      fontWeight: getFontWeight(headings.fontWeight),
      lineHeight: 64 / 48,
      fontSize: `${headingFontSizeNum * headingH2Multiplier}rem`,
      ...responsiveFontSizes({ 
        sm: headingBasePx * 1.0, 
        md: headingBasePx * 1.1, 
        lg: headingBasePx * 1.2 
      }),
    },
    h3: {
      fontWeight: getFontWeight(headings.fontWeight),
      lineHeight: 1.5,
      fontSize: `${headingFontSizeNum * headingH3Multiplier}rem`,
      ...responsiveFontSizes({ 
        sm: headingBasePx * 0.65, 
        md: headingBasePx * 0.75, 
        lg: headingBasePx * 0.8 
      }),
    },
    h4: {
      fontWeight: getFontWeight(headings.fontWeight),
      lineHeight: 1.5,
      fontSize: `${headingFontSizeNum * headingH4Multiplier}rem`,
      ...responsiveFontSizes({ 
        sm: headingBasePx * 0.5, 
        md: headingBasePx * 0.6, 
        lg: headingBasePx * 0.6 
      }),
    },
    h5: {
      fontWeight: getFontWeight(headings.fontWeight),
      lineHeight: 1.5,
      fontSize: `${headingFontSizeNum * headingH5Multiplier}rem`,
      ...responsiveFontSizes({ 
        sm: headingBasePx * 0.475, 
        md: headingBasePx * 0.5, 
        lg: headingBasePx * 0.5 
      }),
    },
    h6: {
      fontWeight: getFontWeight(headings.fontWeight),
      lineHeight: 28 / 18,
      fontSize: `${headingFontSizeNum * headingH6Multiplier}rem`,
      ...responsiveFontSizes({ 
        sm: headingBasePx * 0.45, 
        md: headingBasePx * 0.45, 
        lg: headingBasePx * 0.45 
      }),
    },
    subtitle1: {
      fontWeight: getFontWeight(subheadings.fontWeight),
      lineHeight: 1.5,
      fontSize: `${subheadings.fontSize}rem`,
    },
    subtitle2: {
      fontWeight: getFontWeight(subheadings.fontWeight),
      lineHeight: 22 / 14,
      fontSize: `${subheadingFontSizeNum * subheadingSubtitle2Multiplier}rem`,
    },
    body1: {
      fontWeight: getFontWeight(body.fontWeight),
      lineHeight: 1.5,
      fontSize: `${body.fontSize}rem`,
    },
    body2: {
      fontWeight: getFontWeight(body.fontWeight),
      lineHeight: 22 / 14,
      fontSize: `${body.fontSize}rem`,
    },
    caption: {
      fontWeight: getFontWeight(labels.fontWeight),
      lineHeight: 1.5,
      fontSize: `${labels.fontSize}rem`,
    },
    overline: {
      // Used for column headings/table headers
      fontWeight: getFontWeight(columnHeadings.fontWeight),
      lineHeight: 1.5,
      fontSize: `${columnHeadings.fontSize}rem`,
      textTransform: 'none',
    },
    button: {
      fontWeight: 700,
      lineHeight: 24 / 14,
      fontSize: `${body.fontSize}rem`,
      textTransform: 'capitalize',
    },
    // Additional custom variants for specific UI elements
    // Note: toastText controls toast notification styling (applied via ToastContainer global styles)
    toast: {
      fontWeight: getFontWeight(toastText.fontWeight),
      fontSize: `${toastText.fontSize}rem`,
    },
  };
};

export default getTypographyConfig;
